import { useAuth } from './useAuth';

/**
 * Licensable feature areas, keyed as the backend's `Module` enum serializes them.
 * Canonical constants rather than free strings for the same reason the backend uses
 * an enum: a typo silently reads as "not entitled", which is indistinguishable from
 * a real downgrade.
 */
export const MODULES = {
  notificationRoutes: 'notification_routes',
  paging: 'paging',
  onCall: 'oncall',
  firmware: 'firmware',
  msp: 'msp',
  changeManagement: 'change_management',
} as const;

export type Module = (typeof MODULES)[keyof typeof MODULES];

/**
 * Whether the current organization's plan includes a module.
 *
 * The second of Witina's three gates. A feature renders only if **edition** (from
 * `/api/capabilities` — public, cacheable, compile-time) **and** entitlement (here —
 * per-organization, from the plan) **and** permission (`usePermission` — per-user,
 * from the role) all allow it. They answer different questions and none substitutes
 * for another: edition is what this build can do, entitlement is what this customer
 * bought, permission is what this person may do.
 *
 * Reads the payload `useAuth` already holds, so this costs no fetch.
 *
 * Absent means false. A user still onboarding has no organization and therefore no
 * plan, and hiding gated features is the right answer there — the alternative would
 * offer a control whose first use fails.
 */
export function useEntitlement(module: Module): boolean {
  const { user } = useAuth();
  return user?.entitlements?.[module] ?? false;
}
